import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigationType, useParams } from 'react-router';
import {
  ADD_FAVORITE_PROFESSIONAL,
  BLOCK_RECENT_PROFESSIONAL,
  changeHubFilter,
  FETCH_FAVORITE_PROFESSIONALS,
  FETCH_RECENT_PROFESSIONALS,
  FETCH_RECOMMENDED_PROFESSIONALS_ACTION,
} from '../../actions';
import { clearSuccess } from '../../actions/success';
import { createSuccessSelector } from '../../api/selectors';
import history from '../../browserHistory';
import { Screen, Tab, TabPanel, Tabs, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { CenteredSpinner } from '../../commonComponents';
import { ProfessionalHubRedesign } from '../../constants/ff.posthog';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLoading } from '../../hooks';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useGetPaymentMethods } from '../../domain/Payments';

import { PaymentWarningBanner } from '../PaymentWarningBanner';
import ConfirmPopup from '../shared/ConfirmPopup';
import { TabBadge } from '../TabBadge';
import { FavoritesList } from './components/FavoritesList/FavoritesList';
import { useFavoritesList } from './components/FavoritesList/hooks/useFavoritesList';
import { FilterSelect } from './components/FilterSelect/FilterSelect';
import { useRecentProfessionalsList } from './components/RecentProfessionalsList/hooks/useRecentProfessionalsList';
import { RecentProfessionalsList } from './components/RecentProfessionalsList/RecentProfessionalsList';
import { RecommendedProsFeaturePopup } from './components/RecommendedProsFeaturePopup';
import { useRecommendedProsList } from './components/RecommendedProsList/hooks/useRecommendedProsList';
import { RecommendedProsList } from './components/RecommendedProsList/RecommendedProsList';
import { FavoriteAddedPopup } from './components/shared/FavoriteAddedPopup';
import { getInitialTab, TABS } from './utils';
import { OutlineChevronRightIcon, OutlineMailIcon } from '@tempmee/react-icons';

const actions = [
  FETCH_FAVORITE_PROFESSIONALS,
  FETCH_RECENT_PROFESSIONALS,
  FETCH_RECOMMENDED_PROFESSIONALS_ACTION,
];

const successAddToFavoritesSelector = createSuccessSelector([ADD_FAVORITE_PROFESSIONAL]);
const successBlockProfessionalSelector = createSuccessSelector([BLOCK_RECENT_PROFESSIONAL]);

export const ProfessionalsHub = () => {
  const navType = useNavigationType();
  const params = useParams();
  const { type } = params;
  const dispatch = useDispatch();
  const isAddedToFavoritesSuccess = useSelector((state) => successAddToFavoritesSelector(state));
  const isBlockedProfessionalSuccess = useSelector((state) =>
    successBlockProfessionalSelector(state),
  );
  const { professionalHubFilter: filter, isProfessionalHubInitialized } = useAppSelector(
    (state) => state.user,
  );
  const [showSuccessAddedToFavoritesModal, setShowSuccessAddedToFavoritesModal] = useState(false);
  const [showSuccessBlockProfessionalModal, setShowSuccessBlockProfessionalModal] = useState(false);
  const [activeTab, setActiveTab] = useState(getInitialTab(type));
  const firstRenderRef = useRef(true);
  const initialDataLoadRef = useRef(false);
  const wasLoadingRef = useRef(false);
  const favoritedProfessionalNameRef = useRef(null);

  const isBackNavigation = isProfessionalHubInitialized && navType === 'POP';

  const { isLoading } = useLoading(actions);
  const isNewDesign = useFeatureFlag(ProfessionalHubRedesign);
  useGetPaymentMethods();

  const { favoriteProfessionals, fetchFavoriteProfessionals } = useFavoritesList();
  const { recentProfessionals, fetchRecentProfessionals } = useRecentProfessionalsList();
  const { recommendedProfessionals, fetchRecommendedPros } = useRecommendedProsList();

  const onChangeTab = useCallback((tab) => {
    setActiveTab(tab);
    history.replace(`/professionals-hub/${tab}`);
  }, []);

  useEffect(() => {
    // Reset filter when component unmounts (navigating away from professionals hub)
    return () => {
      dispatch(changeHubFilter('all'));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!firstRenderRef.current) {
      if (isAddedToFavoritesSuccess) {
        setShowSuccessAddedToFavoritesModal(true);
        dispatch(clearSuccess(ADD_FAVORITE_PROFESSIONAL));
      }

      if (isBlockedProfessionalSuccess) {
        setShowSuccessBlockProfessionalModal(true);
        dispatch(clearSuccess(BLOCK_RECENT_PROFESSIONAL));
      }
    } else {
      dispatch(clearSuccess([ADD_FAVORITE_PROFESSIONAL, BLOCK_RECENT_PROFESSIONAL]));
      firstRenderRef.current = false;
    }
  }, [isAddedToFavoritesSuccess, isBlockedProfessionalSuccess, dispatch]);

  useEffect(() => {
    if (isBackNavigation && !isAddedToFavoritesSuccess && !isBlockedProfessionalSuccess) {
      return;
    }

    (async () => {
      await Promise.all([
        fetchFavoriteProfessionals(),
        fetchRecentProfessionals(),
        fetchRecommendedPros(),
      ]);
    })();
  }, [filter, isAddedToFavoritesSuccess, isBlockedProfessionalSuccess]);

  useEffect(() => {
    if (isLoading) {
      wasLoadingRef.current = true;
      return;
    }

    if (!wasLoadingRef.current || isBackNavigation) return;

    // Only auto-select tab on initial data load, not when filter changes
    if (!initialDataLoadRef.current) {
      initialDataLoadRef.current = true;

      if (favoriteProfessionals?.length) {
        onChangeTab(TABS.FAVORITES_LIST);
      } else if (recentProfessionals?.length) {
        onChangeTab(TABS.RECENT_PROFESSIONALS);
      } else if (recommendedProfessionals?.length) {
        onChangeTab(TABS.RECOMMENDED_PROS);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  if (!isNewDesign && isLoading) {
    return (
      <Screen variant="standard">
        <Screen.Header title="Professionals Hub" />
        <Screen.Body>
          <CenteredSpinner minHeight={400} />
        </Screen.Body>
      </Screen>
    );
  }

  return (
    <>
      <Screen variant="standard">
        <Screen.Header title="Professionals Hub" />
        <Screen.Body>
          {isNewDesign && isLoading && <CenteredSpinner />}
          {!(isNewDesign && isLoading) && (
            <>
              {/* LEGACY-HACK (cleanup candidate): bleeds the banner past Screen body padding. */}
              <PaymentWarningBanner
                containerStyle={{ marginTop: '-10px', marginInline: '-10px' }}
              />

              <div
                style={
                  isNewDesign
                    ? {
                        marginTop: 20,
                        display: 'flex',
                        justifyContent: 'space-between',
                        paddingLeft: 80,
                        paddingRight: 80,
                      }
                    : {
                        width: '80%',
                        marginLeft: 80,
                        marginTop: 20,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }
                }
              >
                <FilterSelect />
                <button
                  data-cy="view-invites-button"
                  type="button"
                  style={{
                    all: 'unset',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                  }}
                  onClick={() => history.push('/invites')}
                >
                  <OutlineMailIcon size={24} color={colors.secondary[500]} />
                  {/* fontSize 18 → body_large (16); cursor has no DS prop, kept inline */}
                  <Typography
                    as="span"
                    bold
                    variant="body1"
                    color={colors.secondary['500']}
                    style={{ cursor: 'pointer' }}
                  >
                    View Invites
                  </Typography>
                  <OutlineChevronRightIcon size={10} color={colors.secondary[500]} />
                </button>
              </div>

              <div
                style={
                  isNewDesign
                    ? { marginTop: 40, marginBottom: 60, paddingLeft: 60, paddingRight: 60 }
                    : { width: '80%', alignSelf: 'center', marginTop: 40, marginBottom: 120 }
                }
              >
                <Tabs
                  defaultValue={TABS.FAVORITES_LIST}
                  value={activeTab}
                  onChange={onChangeTab}
                  {...(isNewDesign && { style: { alignItems: 'flex-start', gap: 16 } })}
                >
                  <Tab
                    value={TABS.FAVORITES_LIST}
                    data-cy="favorites-tab"
                    label={
                      <>
                        Favorites <TabBadge count={favoriteProfessionals?.length || undefined} />
                      </>
                    }
                  />
                  <Tab
                    value={TABS.RECENT_PROFESSIONALS}
                    data-cy="worked-with-tab"
                    label={
                      <>
                        Worked With <TabBadge count={recentProfessionals?.length || undefined} />
                      </>
                    }
                  />
                  <Tab
                    value={TABS.RECOMMENDED_PROS}
                    data-cy="new-to-you-tab"
                    label={
                      <>
                        New to You <TabBadge count={recommendedProfessionals?.length || undefined} />
                      </>
                    }
                  />
                  <TabPanel value={TABS.FAVORITES_LIST} style={{ width: '100%' }}>
                    <FavoritesList />
                  </TabPanel>
                  <TabPanel value={TABS.RECENT_PROFESSIONALS} style={{ width: '100%' }}>
                    <RecentProfessionalsList
                      onFavoriteProfessional={(name) => {
                        favoritedProfessionalNameRef.current = name;
                      }}
                    />
                  </TabPanel>
                  <TabPanel value={TABS.RECOMMENDED_PROS} style={{ width: '100%' }}>
                    <RecommendedProsList />
                  </TabPanel>
                </Tabs>
              </div>
            </>
          )}
        </Screen.Body>
      </Screen>

      <RecommendedProsFeaturePopup />

      {showSuccessAddedToFavoritesModal && !isNewDesign && (
        <ConfirmPopup
          dataCy="new-favorite-added-popup"
          closePopupDataCy="new-favorite-modal-backdrop"
          title="New Favorite Added"
          description={
            <>
              This professional has been added onto your
              <br />
              favorites list tab.
            </>
          }
          descriptionStyle={{ marginBottom: 60 }}
          rightButtonText="Close"
          rightButtonAction={() => setShowSuccessAddedToFavoritesModal(false)}
          rightButtonDataCy="new-favorite-close-button"
        />
      )}

      {showSuccessAddedToFavoritesModal && isNewDesign && (
        <FavoriteAddedPopup
          professionalName={favoritedProfessionalNameRef.current ?? ''}
          onClose={() => {
            setShowSuccessAddedToFavoritesModal(false);
            favoritedProfessionalNameRef.current = null;
          }}
        />
      )}

      {showSuccessBlockProfessionalModal && (
        <ConfirmPopup
          dataCy="blocked-professional-popup"
          closePopupDataCy="blocked-professional-modal-backdrop"
          title="Blocked Professional"
          description={
            <>
              This professional has been blocked and will not be able to apply to upcoming shifts.
            </>
          }
          descriptionStyle={{ marginBottom: 60 }}
          rightButtonText="Close"
          rightButtonAction={() => setShowSuccessBlockProfessionalModal(false)}
          rightButtonDataCy="close-button"
        />
      )}
    </>
  );
};
